import _ from "lodash";
import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { Modal, Order, PageMeta } from "src/api/public-types";
import { getConnectedInfoListAsync } from "src/api/question/qeustion-api";
import { WorkOrderListAsync } from "src/api/work-order/workorder-api";
import { WorkOrderListModel, WorkOrderQueryParams, WorkOrderStatus, searchTypes } from "src/api/work-order/workorder-types";
import { BaseButton, BaseCheckbox, BaseInputWithSearch, BaseModal, BaseTable, BaseTooltip } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import RangeDatepicker from "src/components/RangeDatepicker";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import ExcelDownloadButton from "src/pages/visitor/list/ExcelDownloadButton";
import { useModal } from "src/recoil/modalState/hook";
import { qsParse } from "src/utils/common-util";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { workOrderStatus } from "../../workOrder-types";
import WorkOrderListColumn from "./columns/WorkOrderListColumn";

// 연체 필터가 켜져 있으면 이미 종료된 상태는 선택할 수 없습니다.
const OVERDUE_DISABLED_STATUSES: string[] = [WorkOrderStatus.COMPLETED, WorkOrderStatus.CLOSED, WorkOrderStatus.CANCELED];

const WorkOrderListAuthenticated = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 로딩바

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const queryParams: WorkOrderQueryParams = useMemo(() => {
    const _queryParams: WorkOrderQueryParams = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }
    const dump = _.cloneDeep(dateRange);
    if (_queryParams?.startDate !== undefined && _queryParams?.startDate !== "") {
      dump[0] = moment(_queryParams?.startDate).toDate();
    } else {
      dump[0] = null;
    }
    if (_queryParams?.endDate !== undefined && _queryParams?.endDate !== "") {
      dump[1] = moment(_queryParams?.endDate).toDate();
    } else {
      dump[1] = null;
    }

    if (_queryParams?.keywordList) {
      _queryParams.keywordList = decodeURIComponent(_queryParams.keywordList);
    }

    if (_queryParams.statusList && typeof _queryParams.statusList === "string") {
      _queryParams.statusList = _queryParams.statusList.split(",");
    }

    setDateRange(dump);
    return _queryParams;
  }, [location]);

  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });
  const [workOrders, setWorkOrders] = useState<Array<WorkOrderListModel>>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [params, setParams] = useState<WorkOrderQueryParams>({ ...queryParams });
  const { setBaseModal } = useModal();
  const { isAuthority } = usePartnerAuthority();

  // 워크오더 목록 api
  const { executeAsync: getWorkOrderList } = useApiOperation(WorkOrderListAsync);
  const { executeAsync: getConnectedInfoList } = useApiOperation(getConnectedInfoListAsync); // 상담관리 연결정보 조회

  // 워크오더 목록
  const getWorkOrderListApi = useCallback(
    async (workOrderParams: WorkOrderQueryParams) => {
      const response = await getWorkOrderList(workOrderParams);

      const result = response.data.data.content;
      setWorkOrders(result);
      setPageMeta(response.data.meta.pageMeta);
      if (result) {
        const {
          data: {
            data: { content: connectedInfoList },
          },
        } = await getConnectedInfoList({ connectedType: "CS_CONNECTED_WORKORDER" });

        setWorkOrders(
          result.map((item) => ({
            ...item,
            connectedInfoList: connectedInfoList.filter((connectedInfo) => connectedInfo.connectedId === item.id),
          })),
        );
      }
    },
    [getWorkOrderList, location.search],
  );

  useEffect(() => {
    setParams({ ...queryParams });
    getWorkOrderListApi(queryParams);
  }, [getWorkOrderListApi, queryParams]);
  // 쿼리파라미터 정보와 함께 네비게이션

  const navigateWithQueryParams = useCallback(
    (passParams?: WorkOrderQueryParams, type?: "search" | "pagination") => {
      let data;
      if (type) {
        type === "search" ? (data = { ...params }) : (data = { ...queryParams });
      }

      const newQueryParams = { ...data, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params, queryParams],
  );

  // 날짜 검색
  const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
    setDateRange(dateRange);
    let start = "";
    let end = "";

    if (dateRange[0] === null && dateRange[1] === null) {
      setParams({ ...params, ...{ startDate: undefined, endDate: undefined } });
      navigateWithQueryParams({ ...params, ...{ startDate: undefined, endDate: undefined } });
      return;
    }

    if (dateRange[0] !== null) {
      start = moment(dateRange[0]).format("YYYY-MM-DD") + "T00:00:00.000+09:00";
      setParams({ ...params, ...{ startDate: start } });
    }
    if (dateRange[1] !== null) {
      end = moment(dateRange[1]).format("YYYY-MM-DD") + "T23:59:59.999+09:00";
      setParams({ ...params, ...{ startDate: start, endDate: end } });

      navigateWithQueryParams({ ...params, ...{ startDate: start, endDate: end } });
    }
  };

  // 엑셀 다운로드
  const onDownloadWorkOrderExcel = async () => {
    const newParams = { ...params };
    delete newParams.size;

    const response = await getWorkOrderList(newParams);
    const data = response.data.data.content;

    const fileNamePrefix =
      params.startDate && params.endDate
        ? `워크오더-${moment(params.startDate).format("YYYYMMDD")}~${moment(params.endDate).format("YYYYMMDD")}`
        : `워크오더-전체`;

    await downloadExcelByColumns<WorkOrderListModel>({
      data: data,
      columns: WorkOrderListColumn,
      fileNamePrefix,
      onNoData: () => {
        setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
      },
      onSuccess: () => {
        setBaseModal({
          isOpen: true,
          title: "엑셀 다운로드가 완료되었습니다.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
      },
      onError: (error) => {
        console.log("error", error);
        setBaseModal({
          isOpen: true,
          title: "엑셀 다운로드에 실패하였습니다.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
      },
    });
  };
  return (
    <div className="page-product-list">
      {/* 로딩바 */}
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>상태검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <MultiSelectFilter
                    options={workOrderStatus.map((status) => ({
                      value: status.value,
                      label: status.label,
                      isDisabled: params.isOverdue ? OVERDUE_DISABLED_STATUSES.includes(status.value) : false,
                    }))}
                    value={(params.statusList as string[]) || []}
                    onChange={(values) => navigateWithQueryParams({ ...params, page: 0, statusList: values })}
                    placeholder="상태"
                  />
                </div>
                <div className="flex-center">
                  <BaseCheckbox
                    id={"isOverdue"}
                    name={"isOverdue"}
                    label="지연된 업무"
                    className="mr4"
                    checked={params.isOverdue ? true : false}
                    onChange={(checked) => {
                      const newParams = { ...params };

                      if (checked) {
                        navigateWithQueryParams({
                          ...newParams,
                          page: 0,
                          isOverdue: checked ? "true" : "",
                          statusList: ["WORK_ORDER_OPEN", "WORK_ORDER_WAITING", "WORK_ORDER_PROGRESS"],
                        });
                      } else {
                        delete newParams.isOverdue;
                        delete newParams.statusList;
                        navigateWithQueryParams({ ...newParams, page: 0 });
                      }
                    }}
                  />
                  <BaseTooltip
                    touchIcon={"QUESTION"}
                    size={16}
                    tooltip="선택 시 접수/확인/처리중 상태의 업무만 볼 수 있습니다. 다른 상태의 업무를 보시려면 선택을 해제해 주세요."
                  ></BaseTooltip>
                </div>
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType}
                  inputValue={params?.keywordList ? decodeURIComponent(String(params?.keywordList)) : ""}
                  stateOptions={searchTypes}
                  setStateValue={(searchType: string) => {
                    setParams({ ...params, searchType });
                  }}
                  onChange={(keywordList: string) => {
                    setParams({ ...params, keywordList });
                  }}
                  onClearClick={() => {
                    const editParams = { ...params };
                    delete editParams.keywordList;
                    setParams(editParams);
                    navigateWithQueryParams({ ...params, page: 0, keywordList: "" });
                  }}
                  onKeyUp={() => navigateWithQueryParams({ page: 0 }, "search")}
                  onSearchClick={() => navigateWithQueryParams({ page: 0 }, "search")}
                />
                <div className="minmax240">
                  <RangeDatepicker dateRange={dateRange} onChange={handleOnDateRangeChange} placeholder="업무 시작 기간 검색" />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">
            {isAuthority("w") && (
              <BaseButton className="ml20" title="+ 워크시트 생성" onClick={() => navigate(PagePath.workOrder.authenticated.form)} />
            )}
          </div>
        </div>
        <BaseTable
          data={workOrders}
          currentSize={Number(queryParams.size) || 20}
          sizeOption={(sizeValue) => {
            navigateWithQueryParams({ ...params, size: sizeValue, page: 0 });
          }}
          pageIndex={Number(params?.page || 0)}
          totalElements={pageMeta?.totalElements}
          totalPages={pageMeta?.totalPages || 0}
          goPage={(page: number) => {
            navigateWithQueryParams({ page }, "pagination");
          }}
          orders={params?.sort?.orders}
          setOrders={(orders?: Array<Order>) => {
            if (orders) {
              navigateWithQueryParams({ sort: { orders } }, "search");
            }
          }}
          disabledSortHeaders={[
            "workOrderType",
            "summary",
            "endDate",
            "workOrderPriority",
            "mgmtOfficerName",
            "createdBy",
            "delay",
            "buildingName",
            "workOrderSheetGroupId",
            "mgmtOfficeName",
            "costSum",
            "connectedInfoList",
          ]}
          columns={WorkOrderListColumn}
          children={
            <ExcelDownloadButton
              onClick={() => {
                onDownloadWorkOrderExcel();
              }}
            >
              엑셀받기
            </ExcelDownloadButton>
          }
        />{" "}
      </div>
      {alertModal.isOpen && (
        <BaseModal isOpen={true} btnRightTitle="확인" onClick={() => setAlertModal({ isOpen: false })}>
          <p className="font18 ">{alertModal.message}</p>
        </BaseModal>
      )}
    </div>
  );
};

export default WorkOrderListAuthenticated;
